import React, { useState, useEffect } from "react";
import axios from "axios";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { SuccessAlertComponent } from "../odsCalloutBox/SuccessAlertComponent";
import { loadValidationState } from "../../utilities/ValidationStateUtils";
import { combineUrl } from "../../utilities/URLUtils";

interface SubmittedReport {
  id?: number | null;
  status?: string;
  receiptDownloadUrl?: string | null;
  nameOfReport: string;
  callNumber: string;
  errors?: string;
  submittedDate?: string;
  submittedBy?: string;
}

interface Props {
  field: FormSectionElements;
  programContainer: ProgramContainer;
  isEng: boolean;
  callbackElement;
}

interface ComplaintStatusResponse {
  complaintDetailId?: number;
  sendToDCPID?: string;
  status?: string;
  processingStatus?: string;
  isComplete?: boolean;
  message?: string;
}

const normalizeErrorText = (errors: unknown): string => {
  if (typeof errors === 'string') {
    return errors.trim();
  }

  if (!Array.isArray(errors)) {
    return '';
  }

  return errors
    .map((entry: any) => {
      if (typeof entry === 'string') {
        return entry.trim();
      }
      if (entry && typeof entry === 'object') {
        const field = String(entry.fieldName ?? 'Validation').trim();
        const message = String(entry.error ?? entry.message ?? '').trim();
        return message ? `${field}: ${message}` : field;
      }
      return '';
    })
    .filter((part: string) => part.length > 0)
    .join('; ');
};

const splitErrorParts = (errorText: string): string[] => {
  const withLineBreaks = errorText.replace(/\.\s+([A-Za-z][A-Za-z0-9]*:)/g, '.\n$1');
  return withLineBreaks
    .split(/;\s+|\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

const normalizeSubmittedReports = (items: unknown, isEng: boolean): SubmittedReport[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const currentDate = new Date().toLocaleDateString(isEng ? 'en-CA' : 'fr-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const userName = sessionStorage.getItem('userName') || 'User';

  return items.map((r: any) => {
    const errors = normalizeErrorText(r?.errors);
    return {
      id: r?.id ?? null,
      status: String(r?.status ?? (errors ? 'Failed' : 'Success')),
      receiptDownloadUrl: r?.receiptDownloadUrl ?? null,
      nameOfReport: String(r?.nameOfReport ?? ''),
      callNumber: String(r?.callNumber ?? ''),
      errors,
      submittedDate: currentDate,
      submittedBy: userName
    };
  });
};

const IncidentReportConfirmationComponent = ({field, programContainer, isEng}: Props) => {
  const [submittedReports, setSubmittedReports] = useState<SubmittedReport[]>([]);
  const [processingFailureMessage, setProcessingFailureMessage] = useState<string | null>(null);
  const [isProcessingPending, setIsProcessingPending] = useState<boolean>(false);

  useEffect(() => {
    // Load submitted reports from sessionStorage
    if (field.ShowSubmittedTable) {
      const keys = ['submitApiResponse', 'finalReviewData', 'lastApiResponse'];
      for (const key of keys) {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
          continue;
        }
        try {
          const parsed = JSON.parse(raw);
          const normalized = normalizeSubmittedReports(parsed, isEng);
          if (normalized.length > 0) {
            setSubmittedReports(normalized);
            return;
          }
        } catch (e) {
          console.error(`Failed to parse ${key}`, e);
        }
      }

      const state = loadValidationState();
      if (state?.data?.length) {
        setSubmittedReports(normalizeSubmittedReports(state.data, isEng));
      }
    }
  }, [field.ShowSubmittedTable, isEng]);

  useEffect(() => {
    let active = true;

    const getIdentifierFromSubmitResponse = (): string | null => {
      try {
        const submitApiResponse = sessionStorage.getItem('submitApiResponse');
        if (!submitApiResponse) {
          return null;
        }
        const parsed = JSON.parse(submitApiResponse);
        return (
          parsed?.sendToDCPID ||
          parsed?.sendToDcpId ||
          parsed?.complaintDetailId ||
          parsed?.id ||
          null
        );
      } catch {
        return null;
      }
    };

    const statusApiBase = programContainer?.APIBasePath;
    const identifier = getIdentifierFromSubmitResponse();

    if (!statusApiBase || !identifier) {
      return () => {
        active = false;
      };
    }

    const pollStatus = async () => {
      setIsProcessingPending(true);
      setProcessingFailureMessage(null);

      const maxAttempts = 8;
      const delayMs = 1500;

      for (let attempt = 0; attempt < maxAttempts && active; attempt++) {
        try {
          const statusUrl = combineUrl(statusApiBase, `/complaint/status/${encodeURIComponent(String(identifier))}`);
          const response = await axios.get<ComplaintStatusResponse>(statusUrl);
          const statusData = response?.data;
          const normalizedStatus = String(statusData?.status || '').toLowerCase();
          const normalizedProcessingStatus = String(statusData?.processingStatus || '').toLowerCase();

          const isFailed =
            normalizedStatus === 'failed' ||
            normalizedProcessingStatus === 'failed';

          if (isFailed) {
            const fallbackMessage = isEng
              ? 'Incident report submission failed during processing. Please review and try again.'
              : "La soumission du rapport d'incident a échoué pendant le traitement. Veuillez vérifier et réessayer.";
            if (active) {
              setProcessingFailureMessage(statusData?.message || fallbackMessage);
              setIsProcessingPending(false);
            }
            return;
          }

          if (statusData?.isComplete === true) {
            if (active) {
              setIsProcessingPending(false);
            }
            return;
          }
        } catch {
          // Ignore transient status check errors to avoid false negatives in UI
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      if (active) {
        setIsProcessingPending(false);
      }
    };

    pollStatus();

    return () => {
      active = false;
    };
  }, [programContainer?.APIBasePath, isEng]);

  const handleButtonClick = (url: string, clearFormData: boolean = false) => {
    if (url) {
      if (clearFormData) {
        // Clear form-related session data to start fresh
        sessionStorage.removeItem('bxpFormData');
        sessionStorage.removeItem('stepperIndex');
        sessionStorage.removeItem('activeStepList');
        sessionStorage.removeItem('finalReviewData');
        sessionStorage.removeItem('submitApiResponse');
      }
      window.location.href = url;
    }
  };

  const HtmlToReactParser = require('html-to-react').Parser;
  const htmlToReactParser = new HtmlToReactParser();
  const messageHtml = htmlToReactParser.parse(isEng ? field.ErrorMessageRichText : field.ErrorMessageRichTextFRA);

  const errorsBodyTemplate = (rowData: SubmittedReport) => {
    const errorText = normalizeErrorText(rowData.errors);
    if (!errorText) {
      return <span style={{ color: '#6e6e6e' }}>N/A</span>;
    }

    const parts = splitErrorParts(errorText);
    if (parts.length <= 1) {
      return <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{errorText}</span>;
    }

    return (
      <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
        {parts.map((part, i) => (
          <li key={i} style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{part}</li>
        ))}
      </ul>
    );
  };

  const statusBodyTemplate = (rowData: SubmittedReport) => {
    const normalizedStatus = String(rowData.status ?? '').trim().toLowerCase();
    const isFailed = normalizedStatus.includes('fail') || normalizeErrorText(rowData.errors).length > 0;
    return <span>{isFailed ? (isEng ? 'Failure' : 'Échec') : (isEng ? 'Success' : 'Succès')}</span>;
  };

  // Ensure callout is always visible on confirmation page
  field.Visible = true;
  field.InvisibleByDefault = false;

  // Generate dynamic title with count
  const getTitle = () => {
    if (processingFailureMessage) {
      return isEng ? 'Incident report submission failed' : "Échec de la soumission du rapport d'incident";
    }

    if (isProcessingPending) {
      return isEng ? 'Incident report submitted and processing' : "Rapport d'incident soumis et en cours de traitement";
    }

    if (field.ShowSubmittedTable && submittedReports.length > 0) {
      const count = submittedReports.length;
      if (field.DynamicTitleEN && field.DynamicTitleFR) {
        // Use configured dynamic title with {count} placeholder
        const template = isEng ? field.DynamicTitleEN : field.DynamicTitleFR;
        return template.replace('{count}', String(count));
      }
      // Default dynamic title
      return isEng
        ? `${count} incident report${count > 1 ? 's were' : ' was'} successfully submitted`
        : `${count} rapport${count > 1 ? 's d\'incident ont été soumis' : ' d\'incident a été soumis'} avec succès`;
    }
    return isEng ? field.ENUTitle : field.FRATitle;
  };

  return (
    <div>
      <div>
        <h1>{getTitle()}</h1>
      </div>

      {processingFailureMessage && (
        <div className="ontario-alert ontario-alert--error" style={{ marginBottom: '1rem' }}>
          <div className="ontario-alert__body">
            <p style={{ margin: 0 }}>{processingFailureMessage}</p>
          </div>
        </div>
      )}

      <div>
        <SuccessAlertComponent
          field={field}
          title={isEng ? field.callOutBoxTitle : field.callOutBoxTitleFRA}
          bodyMsg={messageHtml}
          isEng={isEng}
          Visible={true}
        />
      </div>

      {/* Submitted Reports Table */}
      {field.ShowSubmittedTable && submittedReports.length > 0 && (
        <div className="ontario-table-container" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
          <div className="ontario-table-div">
            <DataTable
              value={submittedReports}
              className="submitted-reports-table"
              stripedRows
              sortMode="multiple"
            >
              <Column
                field="nameOfReport"
                header={isEng ? "Name of report" : "Nom du rapport"}
                sortable
              />
              <Column
                field="callNumber"
                header={isEng ? "Call number" : "Numéro d'appel"}
                sortable
              />
              <Column
                header={isEng ? "Status" : "Statut"}
                body={statusBodyTemplate}
                sortable
              />
              <Column
                header={isEng ? "Errors" : "Erreurs"}
                body={errorsBodyTemplate}
                style={{ minWidth: '20rem' }}
              />
            </DataTable>
          </div>
        </div>
      )}

      {/* Only show button container if at least one button has a label */}
      {((isEng ? field.TooltipENU : field.TooltipFRA) || (isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA)) && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Button 1 - only show if label is defined */}
          {(isEng ? field.TooltipENU : field.TooltipFRA) && (
            <button
              className="ontario-button ontario-button--primary"
              disabled={false}
              onClick={() => handleButtonClick(isEng ? field.BackUrl : field.BackUrlFRA, true)}
            >
              {isEng ? field.TooltipENU : field.TooltipFRA}
            </button>
          )}
          {/* Button 2 - only show if label is defined */}
          {(isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA) && (
            <button
              className="ontario-button ontario-button--secondary"
              disabled={false}
              onClick={() => handleButtonClick(isEng ? field.FeedbackURL : field.FeedbackURLFRA)}
            >
              {isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default IncidentReportConfirmationComponent;